/**
 * statusIcons.js — [7.0.48] Render-time статус-эмодзи → инлайновый SVG.
 *
 * Почему инлайн, а не спрайт `<use href="#i-…">`:
 *   бейджи качества строятся динамически и вставляются в innerHTML статус-бара
 *   и модалки. Инлайновый <svg> самодостаточен и рендерится независимо от того,
 *   загружен ли спрайт в этом контексте, — не может «не отрисоваться».
 *
 * Конвертирует ТОЛЬКО набор статус-иконок (кружки/галочка/крест/варн/инфо/люди).
 * Прочие эмодзи проходят насквозь без изменений. Текстовые сводки (buildSummary)
 * НЕ трогаем — там эмодзи остаются, SVG в escape-текст не отрисуется.
 */
'use strict';

const dot = (color) =>
  `<svg class="st-ico" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" style="vertical-align:-1px"><circle cx="8" cy="8" r="5" fill="${color}"/></svg>`;

const check = (color) =>
  `<svg class="st-ico" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" style="vertical-align:-2px"><path d="M3.5 8.5l3 3 6-6.5" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const cross = (color) =>
  `<svg class="st-ico" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" style="vertical-align:-2px"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/></svg>`;

const warn = (color) =>
  `<svg class="st-ico" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" style="vertical-align:-2px"><path d="M8 2l6.5 11.5h-13z" fill="none" stroke="${color}" stroke-width="1.6" stroke-linejoin="round"/><path d="M8 6.5v3.2M8 11.6v.1" fill="none" stroke="${color}" stroke-width="1.6" stroke-linecap="round"/></svg>`;

const info = (color) =>
  `<svg class="st-ico" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" style="vertical-align:-2px"><circle cx="8" cy="8" r="6.4" fill="none" stroke="${color}" stroke-width="1.6"/><path d="M8 7v4M8 5v.1" fill="none" stroke="${color}" stroke-width="1.6" stroke-linecap="round"/></svg>`;

const people = (color) =>
  `<svg class="st-ico" viewBox="0 0 20 16" width="16" height="13" aria-hidden="true" style="vertical-align:-2px"><circle cx="7" cy="5" r="2.6" fill="${color}"/><path d="M2.5 13.5c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round"/><circle cx="14" cy="5.5" r="2.1" fill="${color}"/><path d="M12.5 9.7c2.2-.3 4.5 1 4.5 3.8" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round"/></svg>`;

// Цвета согласованы с семантикой светофора приложения.
const STATUS_MAP = {
  '\u{1F7E2}': dot('#22c55e'),   // 🟢
  '\u{1F7E1}': dot('#eab308'),   // 🟡
  '\u{1F7E0}': dot('#f59e0b'),   // 🟠
  '\u{1F534}': dot('#ef4444'),   // 🔴
  '\u{1F7E4}': dot('#a16207'),   // 🟤
  '✅':    check('#22c55e'), // ✅
  '❌':    cross('#ef4444'), // ❌
  '⚠':    warn('#f59e0b'),  // ⚠ (± VS16)
  'ℹ':    info('#3b82f6'),  // ℹ (± VS16)
  '\u{1F465}': people('#94a3b8'),// 👥
};

const STATUS_RE = /\u{1F7E2}|\u{1F7E1}|\u{1F7E0}|\u{1F534}|\u{1F7E4}|✅|❌|\u{1F465}|⚠️?|ℹ️?/gu;

/**
 * Заменяет статус-эмодзи в строке на инлайновый SVG. Остальное — без изменений.
 * @param {string} str
 * @returns {string} HTML (для innerHTML, не textContent!)
 */
export function iconizeStatus(str) {
  if (str == null) return '';
  return String(str).replace(STATUS_RE, (m) => STATUS_MAP[m.replace('️', '')] || m);
}

/**
 * Бейдж качества одного пациента: 〈кружок шума〉 N✓ M✗.
 * Интерполируются только числа → безопасно для innerHTML.
 * @param {{noiseLevel?:string, capabilities?:any[], limitations?:any[]}|null} a
 */
// [11.1.6] qualityBadgeHTML УДАЛЕНА вместе со значком качества в строке
// состояния. Она рисовала «🟡 4 ✓ 3 ✕» — цветной кружок шума и два счётчика,
// без единой подписи. Владелец спросил «что это за кнопочки», и вопрос был
// справедлив: расшифровать три значка с числами было неоткуда.
//
// Оба потребителя (loaderManager и loaderWorkerBridge — там были ДВЕ
// независимые копии одного значка) переведены на показ только счётчика
// пациентов. Держать функцию без потребителей значило бы оставить мёртвый
// код, за которым следующий пойдёт как за живым.

/** Бейдж «N пациентов» с SVG-иконкой людей. */
export function patientsBadgeHTML(n) {
  return `${iconizeStatus('\u{1F465}')} ${n | 0} пациентов`;
}
